import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { staffApi } from "@/api/staff/staff-api";
import type {
  StaffCreate,
  StaffUpdate,
} from "@/types/staff/api-types/staff-types";

interface StaffSearchParams {
  page?: number;
  size?: number;
  first_name?: string;
  last_name?: string;
  level_name?: string;
  active_only?: boolean;
}

export const useGetStaffPaginated = (params: StaffSearchParams = {}) => {
  return useQuery({
    queryKey: ["staff", params],
    queryFn: () => staffApi.getAll(params),
    placeholderData: keepPreviousData,
  });
};

export const useGetStaffServicesSummary = () => {
  return useQuery({
    queryKey: ["staff-services-summary"],
    queryFn: () => staffApi.getServicesSummary(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: StaffCreate) => staffApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["staff-services-summary"] });
    },
  });
};

export const useUpdateStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: StaffUpdate }) =>
      staffApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["staff-services-summary"] });
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => staffApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staff"] });
      queryClient.invalidateQueries({ queryKey: ["staff-services-summary"] });
    },
  });
};
